import {Injectable} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {RemoveConfirmationService} from '@application/helper/remove-confirmation/remove-confirmation.service';
import {AddComponentInput} from '@application/helper/routing/add-component-input.interface';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {Permission} from '@domain/profile/permission.enum';
import {Tool} from '@domain/utilities/tool/tool';
import {HttpToolsService} from '@infrastructure/http/tool/http-tools.service';
import {
  AssertionUtils,
  ContentSwitcherNavigationInput,
  CrudObjectSelectionDialogComponent,
  CrudObjectSelectionDialogInput,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogType,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  TranslateService
} from '@vdw/angular-component-library';
import {Observable} from 'rxjs';
import {AddToolPageComponent} from './add/add-tool-page.component';
import {ToolOverviewService} from './overview/tool-overview.service';

@Injectable({providedIn: 'root'})
export class ToolSelectionService {
  private config: CrudOverviewDataConfig<Tool>;
  private readonly TRANSLATION_KEY = 'UTILITIES.TOOL.TOOL';
  private readonly routes = [
    NavigationUtils.getNavigationRoute(NavigationId.TOOL_MODULE),
    NavigationUtils.getNavigationRoute(NavigationId.TOOL),
    NavigationUtils.getNavigationRoute(NavigationId.ADD_TOOL),
    NavigationUtils.getNavigationRoute(NavigationId.EDIT_TOOL),
    NavigationUtils.getNavigationRoute(NavigationId.DUPLICATE_TOOL)
  ];

  public constructor(
    private readonly tools: HttpToolsService,
    private readonly translate: TranslateService,
    private readonly overview: ToolOverviewService,
    private readonly removeConfirmation: RemoveConfirmationService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<Tool>,
    private readonly navigationHelper: NavigationHelperPrototypeService
  ) {
    this.init();
  }

  public init(): void {
    const addRoute = NavigationUtils.getNavigationRoute(NavigationId.ADD_TOOL);

    const crudOverviewButtonConfigs = this.gridActionButtonsHelper.getActionButtonsWithRouting(
      addRoute.component,
      Permission.TEXSTYLE_PLASTIC_TOOL_EDIT,
      ({gridApi, rowNode}: GridButtonClickedEvent) => {
        const data = AssertionUtils.isNullOrUndefined(rowNode) ? (gridApi?.getSelectedRows()[0] as Tool) : (rowNode.data as Tool);

        if (!AssertionUtils.isEmpty(this.config.selectedObjects) && data.id === this.config.selectedObjects[0]?.id) {
          this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
            titleText: this.translate.instant('CONFLICTS.DIALOG.IN_USE', {object: data.name}),
            messageText: this.translate.instant('CONFLICTS.DIALOG.DESELECT'),
            type: DialogType.DELETE
          });
          return;
        }

        this.removeConfirmation.open(data, this.tools.delete(data.id), this.TRANSLATION_KEY, gridApi, this.tools.getConflicts(data.id));
      }
    );

    this.config = this.builderFactoryService
      .getBuilder()
      .withEntityName(this.TRANSLATION_KEY)
      .withRowData(this.tools.getAll())
      .withDefaultButtons(crudOverviewButtonConfigs)
      .withCrudGridOptions(this.overview.getColumnDefs(true, crudOverviewButtonConfigs.rowActionsComplete), GridIdentifier.TOOL_OVERVIEW, null, (builder: GridOptionsBuilder) =>
        this.overview.getDefaultGridOptionsBuilder(builder).withRowSelection(false, false, true).withLockUsedItem().build()
      )
      .build();
  }

  public openContentSwitcherDialog(inputValue: Tool, plasticProductId?: number, machineId?: number, machineGroupId?: number): Observable<Tool[]> {
    this.config.rowData = null;
    this.config.selectedObjects = [inputValue];
    this.config.dataObservable = this.tools.getCompatible(plasticProductId, machineId, machineGroupId);

    return this.navigationHelper.navigateWithContentSwitcher<Tool[]>({
      componentInfo: {
        component: CrudObjectSelectionDialogComponent,
        componentInputs: {
          config: this.config,
          newClickedEntry: {componentInfo: {component: AddToolPageComponent, componentInputs: {pageType: PageTypeEnum.ADD, canShowHeader: true} as AddComponentInput}} as ContentSwitcherNavigationInput
        } as CrudObjectSelectionDialogInput
      }
    });
  }
}
